import { useStore, useReactFlow } from '@xyflow/react';
import { addDatasetApiKey } from 'js/actions';
import ModalSourceForm from 'js/components/data_sources/add_ds_form/ModalSourceForm';
import { About } from 'js/components/pipelines/cards/components/About';
import {
  ActionItem,
  ActionMenu,
} from 'js/components/pipelines/cards/components/ActionMenu/ActionMenu';
import { NodeBar } from 'js/components/pipelines/cards/components/NodeBar';
import { DATA_RETRIEVER_MIN_WIDTH } from 'js/components/pipelines/FlowNodes/consts';
import { getRelativeNodesBounds } from 'js/components/pipelines/FlowNodes/utils';
import {
  CREATE_FLOW_TRIGGERS_REQUEST,
  DELETE_FLOW_TRIGGERS_REQUEST,
  PUBLISH_RAG_FLOW_REQUEST,
  REFRESH_PIPELINE_REQUEST,
} from 'js/constants/actionTypes';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { getResourceEntity } from 'js/lib/PipelineUtils';
import { isEqual, mapValues, pick } from 'lodash';
import { useDraftContext } from 'new/components/flows/types/genai_rag/context/Draft/useDraftContext';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';
import { ReactComponent as SourceIcon } from 'new/components/icons/fi-rr-exit.svg';
import { ReactComponent as NexsetIcon } from 'new/components/icons/nexsetsOutline.svg';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import ResourceSelectionButton from 'new/elements/ResourceSelectionButton';
import { Stack } from 'new/elements/Stack';
import useEntities from 'new/state/entities/repository/useEntities';
import useFlowTriggersAdapter from 'new/state/flowTriggers/adapter/useFlowTriggersAdapter';
import { useHashedState } from 'new/state/hashedState/repository/useHashedState';
import usePaging from 'new/state/paging/repository/usePaging';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { matchPath } from 'react-router-dom';
import classes from './DataRetriever.module.scss';

interface DataRetrieverProps {
  data: any;
}

const allowedCategories = ['database', 'vectordb', 'rest', 'templatized_api'];

export const DataRetriever = (props: DataRetrieverProps) => {
  const { data: item } = props;
  const dispatch = useAppDispatch();
  const { data: entities } = useEntities();
  const { flowNodes, dataSets } = entities;
  const { createLinks, deleteLinks } = useFlowTriggersAdapter();
  const {
    isEdit,
    draftState,
    draftActions,
    setDraftActions,
    setDraftState,
    addExecuteListener,
    removeExecuteListener,
  } = useDraftContext();

  const [isSourceFormOpen, setIsSourceFormOpen] = useState(false);

  const { refreshPipelineById, publishPipeline } = usePipelinesAdapter();
  const ragFlowId = useRagFlowId();
  const { data: useExpanded } = useHashedState('flows.useExpanded');
  const isPipelineListView = matchPath(
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}`,
    location.pathname,
  )?.isExact;

  const { metaInfoById } = usePaging();
  const branches = useSelector((state: any) => state.pipelines.branches);

  const allowedSourcesForRetrievers = useMemo(() => {
    const connectors = Object.values(metaInfoById);
    return connectors.reduce<string[]>((arr: string[], connector: any) => {
      if (allowedCategories.includes(connector.connectionCategory)) {
        arr.push(connector.key);
      }
      return arr;
    }, []);
  }, [metaInfoById]);

  const rootRAGNode = useSelector((state: any) => state.entities.flowNodes[item.origin_node_id]);
  const { updateNode } = useReactFlow();
  const { minWidth, minHeight } = useStore((store) => {
    const childNodes = Array.from(store.nodeLookup.values()).filter(
      (n) => n.parentId === 'node-' + item.flow_node_id,
    );
    const rect = getRelativeNodesBounds(childNodes);
    const calculatedMinWidth = Math.max(rect.x + rect.width, DATA_RETRIEVER_MIN_WIDTH);

    // console.log('[RUNTIME_WIDTH] DataRetriever display:', {
    //   nodeId: item.flow_node_id,
    //   childNodesCount: childNodes.length,
    //   rectX: rect.x,
    //   rectWidth: rect.width,
    //   rectXPlusWidth: rect.x + rect.width,
    //   DATA_RETRIEVER_MIN_WIDTH,
    //   calculatedMinWidth,
    //   minHeight: rect.y + rect.height,
    //   formula: `Math.max(${rect.x} + ${rect.width}, ${DATA_RETRIEVER_MIN_WIDTH}) = ${calculatedMinWidth}`
    // });

    return {
      minWidth: calculatedMinWidth,
      minHeight: rect.y + rect.height,
      hasChildNodes: childNodes.length > 0,
    };
  }, isEqual);

  // Sync runtime width back to node.data.width so layout honors it
  // This ensures the layout code uses the actual display width, not just the initial calculation
  useEffect(() => {
    if (!updateNode) return; // Guard against missing hook

    const nodeId = 'node-' + item.flow_node_id;
    const currentWidth = (item.width as number) || DATA_RETRIEVER_MIN_WIDTH;

    // Only update if runtime width differs from stored width
    if (minWidth !== currentWidth) {
      // console.log('[SYNC_RUNTIME_WIDTH] Updating node.data.width:', {
      //   nodeId,
      //   oldWidth: currentWidth,
      //   newWidth: minWidth,
      //   runtimeWidth: minWidth,
      //   note: 'This syncs runtime width to node.data.width so layout honors it'
      // });

      updateNode(nodeId, (node) => {
        return {
          ...node,
          data: {
            ...node.data,
            width: minWidth,
          },
        };
      });
    }
  }, [minWidth, item.flow_node_id, item.width, updateNode]);

  const executeDraft = useCallback(async () => {
    if (draftState.flowsPendingLinking) {
      const linkedFlowsIds = draftState.flowsPendingLinking.map(Number);
      linkedFlowsIds.forEach((flowId) => {
        checkDataApi(flowId);
      });
      await refreshPipelineById(rootRAGNode.id);
    }
  }, [flowNodes, dataSets]);

  useEffect(() => {
    addExecuteListener(executeDraft);
    return () => {
      removeExecuteListener(executeDraft);
    };
  }, [executeDraft]);

  const hasLinkedFlows = !!(item?.retriever_flows || [])?.length;

  const checkDataApi = (flowNodeId) => {
    const flowNode = flowNodes[flowNodeId];
    if (flowNode?.children?.length > 0) {
      const nexsetNode = flowNodes[flowNode.children[0]];
      dispatch(addDatasetApiKey(nexsetNode?.data_set_id));
    }
  };

  const onSourceFormDismissed = (source) => {
    setIsSourceFormOpen(false);
    if (source && rootRAGNode) {
      // link the flow of the source to this RAG flow
      const { flow_node_id } = source;
      const existingSourceFlows = rootRAGNode.linked_flows?.filter(
        (flowId) => !flowNodes[flowId]?.data_retriever_ids?.length,
      );
      const existingRetrieverIds =
        rootRAGNode.linked_flows
          ?.filter((flowId) => flowNodes[flowId]?.data_retriever_ids?.length)
          ?.reduce((acc, flowId) => {
            acc.push(...flowNodes[flowId].data_retriever_ids);
            return acc;
          }, []) || [];

      const newDraftState = {
        ...draftState,
        flowLinks: {
          ...draftState.flowLinks,
          [item.id]: [
            ...new Set([
              ...(existingSourceFlows || []),
              ...(draftState.flowLinks?.[item.id] || []),
              flow_node_id,
            ]),
          ],
        },
        flowsPendingLinking: [
          ...new Set([...(draftState.flowsPendingLinking || []), flow_node_id]),
        ],
      };

      setDraftState(newDraftState);

      const flowDraftActions = draftActions[item.id];
      const createLinksIndex = flowDraftActions?.actions?.indexOf(createLinks);
      let createLinksArgs;
      let deleteLinksArgs = {
        flows: [...(newDraftState.flowsPendingDeletion || [])],
        dataSets: [...(newDraftState.dataSetsPendingDeletion || [])],
      };

      if (createLinksIndex > -1) {
        const existingArgs = flowDraftActions?.args?.[createLinksIndex]?.[1] || {};
        const newRetrieverIds = [
          ...(existingArgs?.retriever_data_sets || []),
          ...existingRetrieverIds,
        ];

        createLinksArgs = {
          ...existingArgs,
          linked_flows: [
            ...new Set([
              ...(existingArgs.linked_flows || []),
              ...(existingSourceFlows || []),
              ...(draftState.flowLinks?.[item.id] || []),
              flow_node_id,
            ]),
          ].filter((id) => !deleteLinksArgs?.flows?.includes(id)),
          retriever_data_sets: newRetrieverIds.filter(
            (id) => !deleteLinksArgs?.dataSets?.includes(id),
          ),
        };
      } else {
        createLinksArgs = {
          linked_flows: [
            ...new Set([
              ...(existingSourceFlows || []),
              ...(draftState.flowLinks?.[item.id] || []),
              flow_node_id,
            ]),
          ].filter((id) => !deleteLinksArgs?.flows?.includes(id)),
          retriever_data_sets: (existingRetrieverIds || []).filter(
            (id) => !deleteLinksArgs?.dataSets?.includes(id),
          ),
        };
      }

      setDraftActions({
        ...draftActions,
        [item.id]: {
          args: [[rootRAGNode.id, createLinksArgs], [rootRAGNode.id]],
          actions: [createLinks, refreshPipelineById],
          sync: true,
          requestTypes: [
            ...(draftActions?.[item.id]?.requestTypes || []),
            CREATE_FLOW_TRIGGERS_REQUEST,
            REFRESH_PIPELINE_REQUEST,
            PUBLISH_RAG_FLOW_REQUEST,
          ],
        },
      });
    }
  };

  // If the linked flows doesnt contain an id that is the same as this nexset then it's a linked flow (full)
  const alreadyLinkedFlows = (rootRAGNode?.linked_flows || []).filter(
    (id) => !flowNodes[id]?.data_retriever_ids?.length,
  );

  // If the linked flows contains an id that is the same as this nexset then it's a linked nexset
  const alreadyLinkedNexsets = (rootRAGNode?.linked_flows || []).reduce((arr, id) => {
    arr.push(...(flowNodes[id]?.data_retriever_ids || []));
    return arr;
  }, []);

  const nexsetsInLinkedFlows = useMemo(() => {
    return alreadyLinkedFlows.reduce((arr, id) => {
      const branch = branches[id];
      if (!branch) {
        return arr;
      }
      const pipelineItems = mapValues(pick(flowNodes, branch.items), (flowNode) =>
        getResourceEntity(flowNode, entities),
      );
      arr.push(
        ...Object.values(pipelineItems || {})
          .filter((item) => item.type === 'DATA_SET')
          .map((item) => item?.id),
      );
      return arr;
    }, []);
  }, [entities, flowNodes, rootRAGNode]);

  const nexsetsInRetriever = [...nexsetsInLinkedFlows];

  const onDataSetsSelected = (selection) => {
    if (selection?.length && rootRAGNode) {
      // link the data sets to this RAG flow using retriever_data_sets
      const { flowNodeIds: originNodeIds, dataSetIds } = selection.reduce(
        (acc, { id, origin_node_id }) => {
          const { flowNodeIds, dataSetIds } = acc;
          flowNodeIds.push(origin_node_id);
          dataSetIds.push(id);
          return acc;
        },
        { flowNodeIds: [], dataSetIds: [] },
      );

      // fetch the flows of the selected data sets
      Promise.all(originNodeIds.map((flowNodeId) => refreshPipelineById(flowNodeId)));

      const newDraftState = {
        ...draftState,
        dataSetsPendingLinking: [
          ...new Set([...(draftState.dataSetsPendingLinking || []), ...dataSetIds]),
        ],
      };

      setDraftState(newDraftState);

      const flowDraftActions = draftActions[item.id];
      const createLinksIndex = flowDraftActions?.actions?.indexOf(createLinks);
      let createLinksArgs;
      let deleteLinksArgs = {
        flows: [...(newDraftState.flowsPendingDeletion || [])],
        dataSets: [...(newDraftState.dataSetsPendingDeletion || [])],
      };
      if (createLinksIndex > -1) {
        const existingArgs = flowDraftActions?.args?.[createLinksIndex]?.[1] || {};
        createLinksArgs = {
          linked_flows: [
            ...new Set([...(existingArgs?.linked_flows || []), ...alreadyLinkedFlows]),
          ].filter((id) => !deleteLinksArgs?.flows?.includes(id)),
          retriever_data_sets: [
            ...new Set([
              ...(existingArgs.retriever_data_sets || []),
              ...alreadyLinkedNexsets,
              ...dataSetIds,
            ]),
          ].filter((id) => !deleteLinksArgs?.dataSets?.includes(id)),
        };
      } else {
        createLinksArgs = {
          retriever_data_sets: [...new Set([...dataSetIds, ...alreadyLinkedNexsets])].filter(
            (id) => !deleteLinksArgs?.dataSets?.includes(id),
          ),
          linked_flows: [...new Set(alreadyLinkedFlows)].filter(
            (id) => !deleteLinksArgs?.flows?.includes(id),
          ),
        };
      }

      setDraftActions({
        ...draftActions,
        [item.id]: {
          args: [[rootRAGNode.id, createLinksArgs], [rootRAGNode.id]],
          actions: [createLinks, refreshPipelineById],
          sync: true,
          requestTypes: [
            DELETE_FLOW_TRIGGERS_REQUEST,
            CREATE_FLOW_TRIGGERS_REQUEST,
            REFRESH_PIPELINE_REQUEST,
            PUBLISH_RAG_FLOW_REQUEST,
          ],
        },
      });
    }
  };

  const onOpenSourceForm = (onCloseOptions) => () => {
    setIsSourceFormOpen(true);
    onCloseOptions();
  };

  const onCloseSourceForm = () => {
    setIsSourceFormOpen(false);
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        <NodeBar title="Data Retriever">
          {isEdit ? (
            <ActionMenu wrapPosition>
              {({ onCloseOptions }) => [
                <ActionItem
                  onClick={onOpenSourceForm(onCloseOptions)}
                  icon={<SourceIcon style={{ fill: 'black' }} />}
                >
                  Add a Data Retriever
                </ActionItem>,
                // I had to add this stack to prevent the menu from stealing the focus when typing special characters
                <Stack
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <ResourceSelectionButton
                    hideSelection
                    onClose={onCloseOptions}
                    ButtonComponent={
                      <ActionItem icon={<NexsetIcon style={{ fill: 'black' }} />}>
                        Select a Nexset
                      </ActionItem>
                    }
                    multi
                    hideDetails
                    isItemDisabled={(i) => {
                      return (
                        i.flow_node_id === item.flow_node_id ||
                        alreadyLinkedNexsets.includes(i.id) ||
                        nexsetsInRetriever.includes(i.id)
                      );
                    }}
                    onChoose={(selection) => {
                      onDataSetsSelected(selection);
                      onCloseOptions();
                    }}
                    onRemove={() => {}}
                    resourceType={RESOURCE_TYPE_DATA_SET}
                    dataFeedCompatible
                  />
                </Stack>,
              ]}
            </ActionMenu>
          ) : null}
        </NodeBar>
        <div
          className={classes.nodesContainer}
          style={{
            minHeight,
            minWidth,
            marginTop: hasLinkedFlows ? 0 : 12,
            maxWidth: !hasLinkedFlows ? DATA_RETRIEVER_MIN_WIDTH : undefined,
          }}
        >
          {!hasLinkedFlows && !!ragFlowId && (
            <About>
              Connect to one or more data sources to retrieve relevant documents or records based on
              the query.
            </About>
          )}
          {useExpanded && !ragFlowId && (
            <About>If you have Data Retrievers they will be showed as links in this view.</About>
          )}
          {isPipelineListView && !ragFlowId && !useExpanded && (
            <About>You can't see other Data Retrievers in this view.</About>
          )}
        </div>
      </div>

      <ModalSourceForm
        flowType="genai_rag"
        open={isSourceFormOpen}
        connectionTypes={allowedSourcesForRetrievers}
        onDismiss={(source) => {
          onSourceFormDismissed(source);
        }}
        onClose={onCloseSourceForm}
      />
    </>
  );
};
